import { useCallback, useMemo } from "react"

import { CACHE_PROFILES } from "@/config/query-cache"
import { normalizeTags } from "@/tags/values"
import {
  transport,
  useTransportCollectionQuery,
  useTransportQueries,
  type TransportCollectionData,
  type TransportRequestError,
} from "@/transport"
import type { RefetchOptions, UseQueryResult } from "@tanstack/react-query"

import type { MetricRecordData } from "@/types/metrics/contracts"

import {
  mapTransportMetricsToRecordData,
  METRICS_LIST_TRANSPORT_REQUEST,
  type ListMetricsTransportRequest,
  type TransportMetricResource,
} from "../utils/transportMetricData"

type MetricsData = Record<string, MetricRecordData & { id: string }>

type MetricsTransportData = TransportCollectionData<
  "listMetrics",
  TransportMetricResource
>

type MetricsListResult<T> = Omit<
  UseQueryResult<MetricsTransportData, TransportRequestError>,
  "data" | "isLoading"
> & {
  data: T | undefined
  isLoading: boolean
  metricIdAliases: Record<string, string>
  unavailableMetricIds: string[]
  failedMetricIds: string[]
}

interface UseMetricsListRecordDataOptions<T> {
  enabled?: boolean
  metricNames?: string[]
  referencedMetricIds?: string[]
  revalidateOnMount?: boolean | "stale"
  select?: (data: MetricsData) => T
  tagFilters?: string[]
}

interface TransportLoadingStateInput {
  data: unknown
  enabled: boolean
  error: unknown
  isLoading: boolean
}

function isMetricTransportQueryLoading({
  data,
  enabled,
  error,
  isLoading,
}: TransportLoadingStateInput): boolean {
  return isLoading || (enabled && data == null && error == null)
}

export function useMetricsListRecordDataTransport<T = MetricsData>(
  options?: UseMetricsListRecordDataOptions<T>,
): MetricsListResult<T> {
  const {
    enabled,
    metricNames,
    referencedMetricIds,
    revalidateOnMount = false,
    select,
    tagFilters,
  } = options ?? {}
  const queryEnabled = enabled ?? true
  const normalizedTagFilters = useMemo(
    () => normalizeTags(tagFilters ?? []),
    [tagFilters],
  )
  const request = useMemo<ListMetricsTransportRequest>(() => {
    if (!metricNames?.length && normalizedTagFilters.length === 0) {
      return METRICS_LIST_TRANSPORT_REQUEST
    }

    return {
      ...METRICS_LIST_TRANSPORT_REQUEST,
      metric_names: metricNames?.length ? metricNames : undefined,
      tag_filters:
        normalizedTagFilters.length > 0
          ? (normalizedTagFilters as ListMetricsTransportRequest["tag_filters"])
          : undefined,
    }
  }, [metricNames, normalizedTagFilters])
  const query = useTransportCollectionQuery<
    "listMetrics",
    TransportMetricResource
  >({
    operation: transport.collections.listMetrics,
    request,
    options: {
      ...CACHE_PROFILES.lookup,
      ...(revalidateOnMount && {
        ...(revalidateOnMount === true && { staleTime: 0 }),
        refetchOnMount: revalidateOnMount === true ? ("always" as const) : true,
      }),
      enabled,
    },
  })
  const lookupIds = useMemo(() => {
    const catalogIds = new Set(
      query.data?.items.map((metric) => metric.id) ?? [],
    )
    return [...new Set(referencedMetricIds ?? [])].filter(
      (id) => revalidateOnMount || !catalogIds.has(id),
    )
  }, [query.data, referencedMetricIds, revalidateOnMount])
  const requests = useMemo(
    () => lookupIds.map((id) => ({ metric_id: id })),
    [lookupIds],
  )
  const referencedQueries = useTransportQueries({
    operation: "getMetric",
    ignoreNotFound: true,
    requests,
    ...(revalidateOnMount && { revalidateOnMount }),
    enabled: queryEnabled && (query.data !== undefined || query.isError),
  })
  const catalogItems = useMemo(() => {
    if (!revalidateOnMount) {
      return query.data?.items
    }
    // Confirmed per-ID results own saved selections; transient failures retain catalog definitions.
    const referenceIds = new Set([
      ...referencedQueries.responses.map(({ request }) => request.metric_id),
      ...(referencedQueries.unavailableRequests ?? []).map(
        (request) => request.metric_id,
      ),
    ])
    return query.data?.items.filter((metric) => !referenceIds.has(metric.id))
  }, [
    query.data,
    revalidateOnMount,
    referencedQueries.responses,
    referencedQueries.unavailableRequests,
  ])
  const hasSettledEmptyReferences = Boolean(
    queryEnabled && query.isError && referencedMetricIds?.length === 0,
  )
  const metricRecords = useMemo(
    () =>
      catalogItems ||
      referencedQueries.data.length > 0 ||
      (referencedQueries.unavailableRequests?.length ?? 0) > 0 ||
      hasSettledEmptyReferences
        ? mapTransportMetricsToRecordData([
            ...(catalogItems ?? []),
            ...referencedQueries.data.map(
              (response) => response.metric as TransportMetricResource,
            ),
          ])
        : undefined,
    [
      catalogItems,
      referencedQueries.data,
      referencedQueries.unavailableRequests,
      hasSettledEmptyReferences,
    ],
  )
  const metricIdAliases = useMemo(
    () =>
      Object.fromEntries(
        referencedQueries.responses
          .filter(({ request, data }) => request.metric_id !== data.metric.id)
          .map(({ request, data }) => [request.metric_id, data.metric.id]),
      ),
    [referencedQueries.responses],
  )
  const selectedData = useMemo<T | undefined>(
    () =>
      metricRecords
        ? select
          ? select(metricRecords)
          : (metricRecords as T)
        : undefined,
    [metricRecords, select],
  )

  const unavailableMetricIds = useMemo(
    () =>
      (referencedQueries.unavailableRequests ?? []).map(
        (request) => request.metric_id,
      ),
    [referencedQueries.unavailableRequests],
  )
  const resolvedIds = new Set(
    Object.values(metricRecords ?? {}).map((metric) => metric.id),
  )
  const hasUnresolvedReferences = (referencedMetricIds ?? []).some(
    (id) => !resolvedIds.has(metricIdAliases[id] ?? id),
  )

  const { refetch: refetchCatalog } = query
  const { refetch: refetchReferenced } = referencedQueries
  const refetch = useCallback(
    async (options?: RefetchOptions) => {
      await refetchReferenced()
      return refetchCatalog(options)
    },
    [refetchCatalog, refetchReferenced],
  )

  return {
    ...query,
    refetch,
    data: selectedData,
    metricIdAliases,
    unavailableMetricIds,
    failedMetricIds: (referencedQueries.failedRequests ?? []).map(
      (request) => request.metric_id,
    ),
    error:
      query.error ?? (referencedQueries.error as TransportRequestError | null),
    isError: query.isError || Boolean(referencedQueries.error),
    isLoading:
      (referencedQueries.isLoading &&
        (revalidateOnMount !== "stale" || hasUnresolvedReferences)) ||
      (revalidateOnMount === true &&
        queryEnabled &&
        !query.isFetchedAfterMount &&
        !query.error) ||
      isMetricTransportQueryLoading({
        data: query.data,
        enabled: queryEnabled,
        error: query.error,
        isLoading: query.isLoading,
      }),
  }
}
